<template>
    <div>
        <DsModalHeader fullscreen>
            <template #leading>
                <DsIconButton
                    :dense="false"
                    :disabled="false"
                    :floating="false"
                    name="x"
                    @click="close"
                />
            </template>

            <template #title>
                <DsModalHeaderTitle :title="modalTitle" />
            </template>

            <template #trailing>
                <DsFilledButton
                    v-if="isValidAccount"
                    data-qa="text-voice-then-save-button"
                    @click="clickedSaving"
                >
                    <template #default>
                        {{ $t('funnel.edit.done') }}
                    </template>
                </DsFilledButton>
            </template>
        </DsModalHeader>

        <div v-if="loading" class="ktv-then-spinner">
            <DsSpinner :size="200" />
        </div>

        <div v-else class="ktv-then">
            <div class="ktv-then-content">
                <DsInlineAlert
                    v-if="!isValidAccount"
                    leading-icon
                    data-qa="ktv-disabled-alert"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span class="ktv-disabled-number"> {{ $t('ktvThen.inactiveAccount.messaging') }}
                        <span class="disabled-number">{{ $t('ktvThen.inactiveAccount.unavailable') }} </span>
                        {{ $t('ktvThen.inactiveAccount.text') }}
                    </span>

                    <span class="ktv-disabled-number">
                        {{ $t('ktvThen.inactiveAccount.please') }}
                        <a class="support-link">{{ $t('ktvThen.inactiveAccount.support') }}</a>
                    </span>

                    <DsFilledButton
                        gray
                        class="go-back-button"
                        @click="close"
                    >
                        {{ $t('ktvThen.inactiveAccount.goBack') }}
                    </DsFilledButton>
                </DsInlineAlert>

                <DsInlineAlert
                    v-if="showError && !isPhoneTypesValid"
                    leading-icon
                    data-qa="ktv-phone-type-invalid"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span>{{ $t('ktvThen.KtvInlineAlerts.phoneType') }}</span>
                </DsInlineAlert>

                <DsInlineAlert
                    v-if="showError && !hasMessage"
                    leading-icon
                    data-qa="ktv-message-empty"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span>{{ $t('ktvThen.KtvInlineAlerts.emptyMessage') }}</span>
                    <span>{{ $t('ktvThen.KtvInlineAlerts.emptyMessagePlease') }}</span>
                </DsInlineAlert>

                <DsInlineAlert
                    v-if="showError && !permissionValue"
                    leading-icon
                    data-qa="ktv-no-permission"
                    type="critical"
                    class="ktv-disabled-alert"
                >
                    <span>{{ $t('ktvThen.KtvInlineAlerts.permission') }}</span>
                </DsInlineAlert>

                <KtvPhoneNumber
                    :phone-number="formattedPhoneNumber"
                    class="ktv-card"
                    :class="{'error': showError}"
                    data-qa="ktv-phone-number"
                />

                <KtvPhoneType
                    :phone-types="automationMessage.phoneTypes"
                    :show-error="showError && !isPhoneTypesValid"
                    :saving="saving"
                    :read-only="!isValidAccount"
                    class="ktv-card"
                    data-qa="ktv-phone-type"
                    @input="updatePhoneTypes"
                />

                <KtvEditMessage
                    :message="automationMessage.messageContent"
                    :saving="saving"
                    :content-editable="isValidAccount"
                    :show-error="showError && !isMessageValid"
                    class="ktv-card"
                    data-qa="ktv-edit-message"
                    @message-updated="updateBody"
                />

                <KtvPermission
                    data-qa="ktv-permission"
                    :disabled="!isValidAccount"
                    :permission-value="permissionValue"
                    class="ktv-card"
                    @input="(permission) => { permissionValue = permission }"
                />

                <SmsBroadcastFooter class="ktv-footer" />
            </div>

            <SmsPreview
                class="ktv-then-preview"
                :account="account"
                :message="automationMessage.messageContent"
                :show-opt-in-button="false"
                show-today-date
            />
        </div>
    </div>
</template>

<script>

import KtvPhoneNumber from './components/KtvPhoneNumber.vue';
import KtvPhoneType from './components/KtvPhoneType.vue';
import KtvPermission from './components/KtvPermission.vue';
import KtvEditMessage from './components/KtvEditMessage.vue';
import SmsPreview from '@/components/automatedSms/SmsPreview.vue';
import SmsBroadcastFooter from '@/components/broadcast/sms/SmsBroadcastFooter.vue';
import { containsSupportedCharacters } from '@/components/automatedSms/automatedSms.util';
import {
    getAccountPhoneNumberInfo,
    createAutomationMessage,
    getAutomationMessageInfo,
    updateAutomationMessage,
} from './textVoiceThen.api';
import { utils } from '../../../utils/legacy-utils';

export default {
    components: {
        SmsBroadcastFooter,
        SmsPreview,
        KtvPhoneNumber,
        KtvPhoneType,
        KtvPermission,
        KtvEditMessage,
    },
    props: {
        cell: {
            type: Object,
            default: () => ({}),
        },
        modalTitle: {
            type: String,
            default: '',
        },
        funnelId: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            account: {
                id: 0,
                status: '',
                phoneNumber: '',
                phoneNumberId: 0,
                phoneNumberStatus: '',
                optInMessageIntro: '',
                optInMessage: '',
                optInConfirmationMessage: '',
            },
            automationMessage: {
                id: this.cell?.value?.externalId ?? 0,
                messageContent: '',
                phoneTypes: [],
            },
            permissionValue: false,
            showError: false,
            saving: false,
            loading: true,
        };
    },

    computed: {
        isPhoneTypesValid() {
            return !!this.automationMessage.phoneTypes.length;
        },

        isMessageTooLong() {
            return this.automationMessage.messageContent?.length > this.maxCharacterCount;
        },

        hasMessage() {
            return !!this.automationMessage.messageContent?.length;
        },

        isMessageValid() {
            return !!this.automationMessage.messageContent?.length && !this.isMessageTooLong && this.messageContainsValidCharacters;
        },

        messageContainsValidCharacters() {
            return containsSupportedCharacters(this.automationMessage.messageContent);
        },

        formattedPhoneNumber() {
            let phone = this.account?.phoneNumber;

            if (!phone) {
                return '';
            }

            let numbers = phone.replace(/\D/g, '');

            if (numbers.startsWith('1')) {
                numbers = numbers.substring(1);
            }

            const char = { 0: '(', 3: ') ', 6: '-' };

            phone = '';

            for (let i = 0; i < numbers.length; i++) {
                phone += (char[i] || '') + numbers[i];
            }

            return phone;
        },

        isValidAccount() {
            return this.account.id !== 0 && this.account.status === 'ACTIVE'
                && this.account.phoneNumberStatus === 'ACTIVE'
                && !!this.account.phoneNumber;
        },

        isConfigurationValid() {
            return this.isPhoneTypesValid && this.isMessageValid && this.permissionValue;
        },
    },

    async mounted () {
        const { accountPhoneNumber } = await getAccountPhoneNumberInfo();

        if (accountPhoneNumber) {
            this.account.id = accountPhoneNumber.accountId;
            this.account.status = accountPhoneNumber.accountStatus;
            this.account.phoneNumber = accountPhoneNumber.phoneNumber;
            this.account.phoneNumberId = accountPhoneNumber.phoneNumberId;
            this.account.phoneNumberStatus = accountPhoneNumber.phoneNumberStatus;
        }

        if (this.automationMessage.id) {
            const { automationMessage } = await getAutomationMessageInfo(this.automationMessage.id);

            this.automationMessage.messageContent = automationMessage.body;
            this.automationMessage.phoneTypes = automationMessage.phoneTypes || [];
        } else {
            this.automationMessage.phoneTypes = ['Mobile', 'Work', 'Home', 'Other'];
        }
        this.loading = false;
    },

    methods: {
        updatePhoneTypes(phoneTypes) {
            this.automationMessage.phoneTypes = phoneTypes;

            if (this.isPhoneTypesValid && this.showError) {
                this.showError = false;
            }
        },

        updateBody(message) {
            this.automationMessage.messageContent = message;

            if (this.isMessageValid && this.showError) {
                this.showError = false;
            }
        },

        async saveConfiguration() {
            const payload = {
                accountId: utils.removeLongSuffix(this.account.id),
                body: this.automationMessage.messageContent,
                externalReferenceId: `${this.funnelId}`,
                phoneTypes: this.automationMessage.phoneTypes,
            };

            if (this.automationMessage.id === 0) {
                payload.phoneNumberId = utils.removeLongSuffix(this.account.phoneNumberId);

                const automationMessage = await createAutomationMessage(payload);

                const { id } = automationMessage;

                this.automationMessage.id = id;
            } else {
                payload.automationMessageId = utils.removeLongSuffix(this.automationMessage.id);
                payload.pathList = ['body', 'phoneTypes'];

                await updateAutomationMessage(payload);
            }
        },

        async clickedSaving() {
            if (this.isConfigurationValid) {
                await this.saveConfiguration();

                const event = {
                    data: {
                        externalId: this.automationMessage.id,
                        error: null,
                    },
                };

                this.$emit('update-internal-config', event);
            } else {
                this.showError = true;
            }
        },

        close() {
            this.$emit('close-modal');
        },
    },
};

</script>

<style scoped lang="scss">

.ktv-then {
    display: flex;
    margin: var(--modal-padding, 1.5rem);
}

.ktv-then-spinner {
    margin-top: $spacing-800;
    display: flex;
    flex-direction: column;
    align-items: center;
    vertical-align: middle;
}

.ktv-then-content {
    margin-top: $spacing-400;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 3;
}

.ktv-then-preview {
    flex: 2;
}

.ktv-component {
    display: flex;
    flex-direction: column;
}

.ktv-card {
    margin-bottom: $spacing-400;
    width: 52%;
}

.ktv-card.error {
    margin-top: $spacing-300;
}

.ktv-footer {
    width: 52%;
    padding-bottom: $spacing-200;
    font-size: $font-size-xs;
    gap: $spacing-100;
    color: $color-gray-700;
    background-color: $color-white;
    display: flex;
    flex-direction: column;
}

.ktv-disabled-alert {
    margin-bottom: $spacing-100;
    width: 52%;
}

.ktv-disabled-number {
    color: $color-ink-900;
    font-family: $font-family;
    font-size: $font-size-sm;
    font-weight: $font-weight-regular;
    line-height: $line-height-md;
}

.disabled-number {
    color: $color-red-800;
    font-weight: $font-weight-bold;
}

.support-link {
    text-decoration: underline;
}

.go-back-button {
    align-self: flex-start;
    margin-top: $spacing-100;
}

</style>
